import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import {
  clearProjectCommand,
  completeProjectCommand,
  pendingProjectCommand,
} from '@/services/projectCommands';
import type { ProjectCommand } from '@/types/project-command';
import type { OperationView } from '@/types/session';
import { ZodError } from 'zod';

export function useProjectCommand(projectId?: string) {
  const principal = useAuthStore((state) => state.session?.principalId);
  const queryClient = useQueryClient();
  const [saved] = useState(() => {
    try {
      return {
        request: principal ? pendingProjectCommand(principal, projectId) : null,
        error: '',
      };
    } catch (error) {
      return { request: null, error: (error as Error).message };
    }
  });
  const [pending, setPending] = useState(!!saved.request);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(saved.error);
  const [status, setStatus] = useState('');
  const [result, setResult] = useState<OperationView | null>(null);
  const controller = useRef<AbortController>();
  const running = useRef(false);
  useEffect(() => () => controller.current?.abort(), []);
  const run = async (input: ProjectCommand | null) => {
    if (!principal || running.current || saved.error) return;
    running.current = true;
    setBusy(true);
    setError('');
    setStatus('');
    setResult(null);
    const current = new AbortController();
    controller.current = current;
    try {
      const requestKind =
        pendingProjectCommand(principal, projectId)?.command.kind ??
        input?.kind;
      const operation = await completeProjectCommand(
        principal,
        input,
        projectId,
        setStatus,
        current.signal
      );
      if (
        current.signal.aborted ||
        useAuthStore.getState().session?.principalId !== principal
      )
        return;
      if (operation) {
        setResult(operation);
        setStatus(
          projectId === 'bramp'
            ? 'Ramp request confirmed.'
            : projectId === 'skill-requests'
              ? 'Skill request recorded. Awaiting operator review.'
              : 'Project request confirmed.'
        );
        // Keep creation recoverable until the user opens the created project.
        if (projectId && requestKind !== 'completion-submit')
          clearProjectCommand(principal, projectId);
        if (projectId === 'skill-requests')
          await queryClient.invalidateQueries({
            queryKey: ['current-skill-requests', principal],
          });
        await queryClient.invalidateQueries({
          queryKey: ['current-projects', principal],
        });
        await queryClient.invalidateQueries({ queryKey: ['current-disputes'] });
        await queryClient.invalidateQueries({
          queryKey: ['current-finance', principal],
        });
      }
      return operation;
    } catch (reason) {
      if (!current.signal.aborted) {
        setError(
          reason instanceof ZodError
            ? reason.issues
                .map((issue) => issue.path.join('.') + ': ' + issue.message)
                .join('; ')
            : reason instanceof Error
              ? reason.message
              : 'Project request failed.'
        );
        await queryClient.invalidateQueries({
          queryKey: ['current-projects', principal],
        });
        await queryClient.invalidateQueries({ queryKey: ['current-disputes'] });
      }
    } finally {
      running.current = false;
      if (
        !current.signal.aborted &&
        useAuthStore.getState().session?.principalId === principal
      ) {
        try {
          setPending(!!pendingProjectCommand(principal, projectId));
        } catch {
          setPending(true);
        }
        setBusy(false);
      }
    }
  };
  return {
    run,
    finishSubmission: () => {
      if (
        principal &&
        projectId &&
        useAuthStore.getState().session?.principalId === principal
      ) {
        clearProjectCommand(principal, projectId);
        setPending(false);
      }
    },
    pending,
    busy,
    error,
    status,
    result,
    saved: saved.request,
    blocked: !!saved.error,
  };
}
